import React, {FC} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  ExportContactsCsvJobModel,
  JobStatusesEnum,
} from '../../../../../../libs/shared/core/src/models/api/jobModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {Banner, BannerTypesEnum} from '../../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerProgress} from '../../../../../../libs/shared/ui/src/components/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerSecondaryText} from '../../../../../../libs/shared/ui/src/components/banners/bannerSecondaryText';
import {BannerText} from '../../../../../../libs/shared/ui/src/components/banners/bannerText';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {downloadFileFromUrl} from '../../../../../helpers/fileHelpers';

/*
 * Props.
 */

interface ContactsCsvExportBannerProps {
  job: ExportContactsCsvJobModel;
  progress: number;
  downloadUrl: string;
  onDeleteJob: (arg: number) => void;
}

/*
 * Component.
 */

export const ContactsCsvExportBanner: FC<ContactsCsvExportBannerProps> = (props) => {
  const {job, progress, downloadUrl, onDeleteJob} = props;

  const onDismissClick = () => onDeleteJob(job.id);
  const onDownloadClick = () => {
    try {
      downloadFileFromUrl(document, downloadUrl, `contacts_export_${job.id}`);
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  if (job.status === JobStatusesEnum.FAILED) {
    return renderErrorBanner(onDismissClick);
  }

  if (job.status === JobStatusesEnum.DONE) {
    return renderCompletedBanner(onDismissClick, onDownloadClick);
  }

  return renderProgressBanner(progress);
};

/*
 * Helpers.
 */

function renderProgressBanner(progress: number) {
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>
        <Trans id="banner-contacts-csv-export-generating">Generating contacts export...</Trans>
      </BannerText>
      <BannerSecondaryText>
        <Trans id="banner-contacts-csv-export-progress">{Math.floor(100 * progress)}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
}

function renderErrorBanner(onDismissClick: () => void) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="warningFilledSmall" />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-contacts-csv-export-failed">CSV export failed.</Trans>
            </BannerContentTitle>
            <Trans id="banner-contacts-csv-export-failed-try-again-message">Please try again.</Trans>
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderCompletedBanner(onDismissClick: () => void, onDownloadClick: () => void) {
  return (
    <Banner bannerType={BannerTypesEnum.MULTILINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerIcon iconName="contactsFilledSmall" />
      <BannerContent>
        <BannerContentTitle>
          <Trans id="banner-contacts-csv-export-ready">Contacts export ready</Trans>
        </BannerContentTitle>
      </BannerContent>
      <BannerFooterActions>
        <BannerAction onClick={onDownloadClick}>
          <Trans id="banner-contacts-csv-export-download">DOWNLOAD</Trans>
        </BannerAction>
      </BannerFooterActions>
      <BannerCloseAction onClick={onDismissClick} />
    </Banner>
  );
}
